import { Children, type ReactNode } from 'react';
import { cn } from './cn.js';

/**
 * As classes da grade, separadas do componente para terem teste.
 *
 * A suíte deste pacote roda em `node` e não tem DOM — renderizar `CardGrid`
 * para conferir quantas colunas saíram não é possível aqui. Com a régua numa
 * função pura, o caso de um cartão e o de três são dois `expect`, e o
 * componente vira a casca que conta os filhos.
 *
 * É o mesmo desenho de `cardClasses` e `buttonClasses`.
 */
export function cardGridClasses(cards: number, className?: string): string {
  return cn(
    'grid grid-cols-1 gap-3',
    /*
     * `> 1` e não `>= 1`: com um cartão a segunda trilha existiria vazia, e o
     * cartão sairia com metade da largura ao lado de uma sobra em branco — a
     * tela dizendo que falta alguma coisa ali.
     */
    cards > 1 && 'lg:grid-cols-2',
    className,
  );
}

/**
 * A grade determinística de cartões — **duas colunas, e a última linha não
 * estica**.
 *
 * ## Por que ela não é `auto-fit` nem `auto-fill`
 *
 * `StatCardList` deixa o navegador decidir quantas trilhas cabem, e ali isso é
 * certo: o cartão de número tem largura mínima conhecida, e o painel inteiro
 * precisa da mesma. Aqui a pergunta é outra — o cartão carrega texto corrido,
 * então não há largura mínima que signifique alguma coisa, e deixar o CSS
 * escolher faria a mesma tela mostrar duas colunas num monitor e três noutro,
 * com o conteúdo idêntico.
 *
 * A régua é a contagem, e só ela:
 *
 * - **um** cartão ocupa a linha inteira;
 * - **dois ou mais** vão a duas colunas, e o terceiro desce para a linha
 *   seguinte com metade da largura — nunca esticado para cobrir a sobra.
 *
 * O ímpar é o caso que as grades automáticas erram: com três cartões,
 * `auto-fit` colapsa a trilha vazia e alarga o terceiro até 100%, e a tela
 * passa a dizer que aquele cartão importa mais que os outros dois.
 *
 * ## O corte é `lg`, e ele não é uma terceira regra — é o piso
 *
 * Abaixo de 1024px a grade cai para uma coluna. É a medição que
 * `RecurrenceSection` já carregava no comentário dela quando escrevia a grade à
 * mão: dois destes cartões lado a lado em 640px atropelam título, contexto e
 * valor na mesma linha.
 *
 * ## Quem quer a vertical não usa esta grade
 *
 * Empilhar é `flex flex-col gap-3`, e é o que a Carteira faz. Um parâmetro
 * `columns={1}` aqui daria duas formas de escrever a mesma pilha, e a segunda é
 * a que diverge — foi assim que "caixa branca com borda" chegou a sete formas
 * antes do `Card`.
 */
export function CardGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  /*
   * `Children.toArray` e não `Children.count`: o primeiro descarta `null`,
   * `undefined` e booleano, que é o que um `{condicao && <Card/>}` deixa na
   * lista. `count` os contaria, e um único cartão ao lado de duas condições
   * falsas nasceria com metade da largura e nada do lado.
   */
  return (
    <div className={cardGridClasses(Children.toArray(children).length, className)}>
      {children}
    </div>
  );
}
